import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, GripVertical, ImagePlus, Trash2 } from 'lucide-react';
import { apiClient } from '../../lib/apiClient';
import { resolveMediaUrl } from '../../lib/mediaUrl';

const MAX_IMAGE_MB = 20;
const field = 'w-full border border-gray-300 rounded-lg px-3 min-h-[40px] bg-white text-sm';

const apiError = (err, fallback) => {
  const d = err.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

let keySeq = 0;
const withKey = (s) => ({ ...s, _key: s.id || `new-${Date.now()}-${keySeq++}` });
const dragCarriesFiles = (e) => [...(e.dataTransfer?.types || [])].includes('Files');

/**
 * Dev -> File Transfer -> Slides: client advertising backgrounds for the landing.
 * Drop images anywhere in the box to upload them; drag a row by its grip to
 * reorder (the arrow buttons do the same for keyboards and touch). Removing a
 * slide and saving deletes its image from storage as well.
 */
const TransferSlidesManager = () => {
  const [slides, setSlides] = useState(null);
  const [saved, setSaved] = useState([]); // the list as last saved, to name what a save would delete
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState('');
  const [fileOver, setFileOver] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    apiClient.get('/transfers/admin/slides').then((r) => { setSlides(r.data.slides.map(withKey)); setSaved(r.data.slides); })
      .catch(() => { setSlides([]); toast.error('Could not load slides'); });
  }, []);

  const change = (next) => { setSlides(next); setDirty(true); };
  const edit = (i, patch) => change(slides.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (from, to) => {
    if (from === to || to < 0 || to >= slides.length) return;
    const next = [...slides];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    change(next);
  };

  const upload = async (files) => {
    const picked = [...files];
    const added = [];
    try {
      for (let n = 0; n < picked.length; n += 1) {
        const file = picked[n];
        setUploading(`Uploading ${n + 1} of ${picked.length}…`);
        if (!file.type.startsWith('image/')) { toast.error(`${file.name} is not an image`); continue; }
        if (file.size > MAX_IMAGE_MB * 1024 * 1024) { toast.error(`${file.name} is over ${MAX_IMAGE_MB} MB`); continue; }
        const form = new FormData();
        form.append('file', file);
        form.append('folder', 'transfer-slides');
        const { data } = await apiClient.post('/storage/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (!data?.url) throw new Error('Upload response missing URL');
        added.push(withKey({ image_url: data.url, advertiser: '', link_url: '', ref_code: '', alt: '', active: true, starts_on: '', ends_on: '' }));
      }
    } catch (err) {
      toast.error(apiError(err, 'Upload failed'));
    } finally {
      setUploading('');
      if (added.length) {
        setSlides((cur) => [...(cur || []), ...added]);
        setDirty(true);
      }
    }
  };

  const remove = (i) => {
    const row = slides[i];
    // A row that was never saved has an uploaded image nothing else knows about:
    // delete it now. A saved slide's image is deleted when the removal is saved.
    if (!row.id) apiClient.post('/transfers/admin/slides/discard', { image_url: row.image_url }).catch(() => {});
    change(slides.filter((_, j) => j !== i));
  };

  // Saving can delete uploaded images for good. The server refuses until it is told to go ahead
  // (409 confirm_delete), so we ask the person, naming what would go, and only then confirm.
  const save = async (confirmDelete = false) => {
    try {
      const payload = slides.map(({ _key, ...s }) => s);
      const { data } = await apiClient.put('/transfers/admin/slides', { slides: payload, confirm_delete: confirmDelete });
      setSlides(data.slides.map(withKey));
      setSaved(data.slides);
      setDirty(false);
      toast.success(data.deleted ? `Slides saved. ${data.deleted} removed image${data.deleted === 1 ? '' : 's'} deleted from storage.` : 'Slides saved');
      if (data.delete_failed) {
        toast.warning(`${data.delete_failed} image${data.delete_failed === 1 ? '' : 's'} could not be deleted from storage yet. It will be retried automatically.`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'confirm_delete') {
        const names = detail.images.map((u) => {
          const slide = saved.find((s) => s.image_url === u);
          return `• ${slide?.advertiser || 'Slide'} (${u.split('/').pop()})`;
        });
        const shown = names.slice(0, 6).join('\n') + (names.length > 6 ? `\n…and ${names.length - 6} more` : '');
        const word = detail.count === 1 ? 'image' : 'images';
        if (window.confirm(`Saving will PERMANENTLY delete ${detail.count} uploaded ${word} from storage:\n\n${shown}\n\nThis cannot be undone. Delete and save?`)) {
          save(true);
        }
        return;
      }
      toast.error(apiError(err, 'Could not save slides'));
    }
  };

  // ---- file drops (upload) ----
  const onZoneDragOver = (e) => { if (dragIndex === null && dragCarriesFiles(e)) { e.preventDefault(); setFileOver(true); } };
  const onZoneDrop = (e) => {
    if (dragIndex !== null || !dragCarriesFiles(e)) return;
    e.preventDefault();
    setFileOver(false);
    upload(e.dataTransfer.files);
  };

  if (!slides) return <p className="text-sm text-gray-500">Loading slides…</p>;

  return (
    <div className="space-y-4" data-testid="transfer-slides-manager"
      onDragOver={onZoneDragOver} onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFileOver(false); }} onDrop={onZoneDrop}>
      <div
        role="button" tabIndex={0} onClick={() => fileRef.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${fileOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
        data-testid="transfer-slide-dropzone">
        <ImagePlus className="w-7 h-7 mx-auto mb-2 text-gray-500" />
        <p className="font-medium text-sm">{uploading || 'Drag images here, or click to choose'}</p>
        <p className="text-xs text-gray-500 mt-1">Wide landscape images work best (about 1920×1080). Up to {MAX_IMAGE_MB} MB each. Add as many as you like.</p>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden
          onChange={(e) => { upload(e.target.files); e.target.value = ''; }} data-testid="transfer-slide-upload" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => save()} disabled={!dirty}
          className="px-4 min-h-[40px] rounded-lg bg-blue-600 text-white text-sm disabled:opacity-40" data-testid="transfer-slides-save">
          Save slides
        </button>
        {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
        <span className="text-xs text-gray-500">Removed slides are deleted from storage when you save. Drag the grip to reorder; leave dates blank to run a slide all the time.</span>
      </div>

      {slides.length === 0 && <p className="text-sm text-gray-500">No slides yet — the page shows a plain gradient.</p>}

      <ul className="space-y-3">
        {slides.map((s, i) => (
          <li key={s._key}
            onDragOver={(e) => { if (dragIndex !== null) { e.preventDefault(); setOverIndex(i); } }}
            onDrop={(e) => { if (dragIndex !== null) { e.preventDefault(); move(dragIndex, i); setDragIndex(null); setOverIndex(null); } }}
            className={`border rounded-lg p-3 grid sm:grid-cols-[1.5rem_9rem_1fr] gap-3 transition-shadow ${
              overIndex === i && dragIndex !== null && dragIndex !== i ? 'ring-2 ring-blue-500' : ''} ${dragIndex === i ? 'opacity-50' : ''}`}
            data-testid="transfer-slide-row">
            <span draggable title="Drag to reorder"
              onDragStart={(e) => {
                setDragIndex(i);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', s._key);
                const row = e.currentTarget.closest('li');
                if (row) e.dataTransfer.setDragImage(row, 24, 24);
              }}
              onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
              className="hidden sm:flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
              data-testid="transfer-slide-handle">
              <GripVertical className="w-5 h-5" />
            </span>
            <img src={resolveMediaUrl(s.image_url)} alt="" className="w-full sm:w-36 h-24 object-cover rounded bg-gray-100" draggable={false} />
            <div className="grid sm:grid-cols-2 gap-2">
              <input className={field} placeholder="Advertiser (shown as “Ad · name”)" maxLength={80}
                value={s.advertiser} onChange={(e) => edit(i, { advertiser: e.target.value })} />
              <input className={field} placeholder="Sponsor link (https://…) - adds a “Visit sponsor” button" maxLength={500}
                value={s.link_url} onChange={(e) => edit(i, { link_url: e.target.value })} />
              <input className={`${field} sm:col-span-2`} placeholder="Referral code (optional) - sent to the sponsor as ref=…" maxLength={60} pattern="[A-Za-z0-9._\-]*"
                title="Letters, numbers, dot, dash and underscore only" value={s.ref_code || ''} onChange={(e) => edit(i, { ref_code: e.target.value })} data-testid="transfer-slide-ref" />
              <input className={`${field} sm:col-span-2`} placeholder="Image description for screen readers (optional)" maxLength={200}
                value={s.alt} onChange={(e) => edit(i, { alt: e.target.value })} />
              <label className="text-xs text-gray-600">Starts
                <input type="date" className={field} value={s.starts_on} onChange={(e) => edit(i, { starts_on: e.target.value })} /></label>
              <label className="text-xs text-gray-600">Ends
                <input type="date" className={field} value={s.ends_on} onChange={(e) => edit(i, { ends_on: e.target.value })} /></label>
              <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-2 text-sm mr-auto">
                  <input type="checkbox" checked={s.active} onChange={(e) => edit(i, { active: e.target.checked })} /> Show this slide
                </label>
                <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => move(i, i - 1)}
                  className="w-10 h-10 inline-flex items-center justify-center rounded border disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button type="button" aria-label="Move down" disabled={i === slides.length - 1} onClick={() => move(i, i + 1)}
                  className="w-10 h-10 inline-flex items-center justify-center rounded border disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button type="button" aria-label="Delete slide" onClick={() => remove(i)} data-testid="transfer-slide-delete"
                  className="w-10 h-10 inline-flex items-center justify-center rounded border text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TransferSlidesManager;
